import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import { inventoryAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

const QUICK_ADD_CONFIGS = {
  itemType: {
    label: 'Item Type',
    emptyForm: { name: '' },
    fields: [{ key: 'name', label: 'Name', required: true }],
    create: (d) => inventoryAPI.createItemType(d),
    getAll: () => inventoryAPI.getItemTypes(),
    setter: 'setItemTypes',
    formField: 'item_type_id',
  },
  manufacturer: {
    label: 'Manufacturer',
    emptyForm: { name: '' },
    fields: [{ key: 'name', label: 'Name', required: true }],
    create: (d) => inventoryAPI.createManufacturer(d),
    getAll: () => inventoryAPI.getManufacturers(),
    setter: 'setManufacturers',
    formField: 'manufacturer_id',
  },
  unit: {
    label: 'Unit',
    emptyForm: { name: '', abbreviation: '' },
    fields: [
      { key: 'name', label: 'Name', required: true },
      { key: 'abbreviation', label: 'Abbreviation' },
    ],
    create: (d) => inventoryAPI.createUnit(d),
    getAll: () => inventoryAPI.getUnits(),
    setter: 'setUnits',
    formField: 'unit_id',
  },
  supplier: {
    label: 'Supplier',
    emptyForm: { name: '', contact: '', phone: '', email: '', address: '', city: '', state: '' },
    fields: [
      { key: 'name', label: 'Name', required: true },
      { key: 'contact', label: 'Contact' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', type: 'select', options: US_STATES },
    ],
    create: (d) => inventoryAPI.createSupplier(d),
    getAll: () => inventoryAPI.getSuppliers(),
    setter: 'setSuppliers',
    formField: 'supplier_id',
  },
};

const emptyForm = {
  item_name: '',
  item_type_id: '',
  unit_id: '',
  supplier_id: '',
  manufacturer_id: '',
  quantity: 0,
  reorder_level: '',
  notes: '',
};

const InventoryPage = () => {
  const notify = useNotify();
  const [items, setItems] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const [quickAdd, setQuickAdd] = useState({ type: null, form: {}, error: '' });

  const listSetters = { setItemTypes, setManufacturers, setUnits, setSuppliers };

  useEffect(() => {
    loadAll();
  }, []);

  useRefreshOnFocus(() => loadAll());

  const loadAll = async () => {
    setLoadError('');
    try {
      const [itemsRes, typesRes, unitsRes, suppliersRes, mfgRes] = await Promise.all([
        inventoryAPI.getAll(),
        inventoryAPI.getItemTypes(),
        inventoryAPI.getUnits(),
        inventoryAPI.getSuppliers(),
        inventoryAPI.getManufacturers(),
      ]);
      setItems(toArray(itemsRes.data, 'inventory items'));
      setItemTypes(toArray(typesRes.data, 'item types'));
      setUnits(toArray(unitsRes.data, 'units'));
      setSuppliers(toArray(suppliersRes.data, 'suppliers'));
      setManufacturers(toArray(mfgRes.data, 'manufacturers'));
    } catch (error) {
      console.error('Error loading inventory:', error);
      // The previous list is left in place on purpose: a failed refresh should say so
      // rather than blank the page out into something indistinguishable from "no items".
      setLoadError(error?.response?.data?.error || 'Unable to load inventory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        item_name: item.item_name || '',
        item_type_id: item.item_type_id || '',
        unit_id: item.unit_id || '',
        supplier_id: item.supplier_id || '',
        manufacturer_id: item.manufacturer_id || '',
        quantity: item.quantity ?? 0,
        reorder_level: item.reorder_level ?? '',
        notes: item.notes || '',
      });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        item_type_id: formData.item_type_id || null,
        unit_id: formData.unit_id || null,
        supplier_id: formData.supplier_id || null,
        manufacturer_id: formData.manufacturer_id || null,
        reorder_level: formData.reorder_level === '' ? null : formData.reorder_level,
      };
      if (editingItem) {
        await inventoryAPI.update(editingItem.id, payload);
      } else {
        await inventoryAPI.create(payload);
      }
      handleCloseDialog();
      loadAll();
    } catch (error) {
      console.error('Error saving inventory item:', error);
      notify.error(errorMessage(error, 'Error saving inventory item'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await inventoryAPI.delete(deleteTarget);
      setDeleteTarget(null);
      loadAll();
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      notify.error(errorMessage(error, 'Error deleting inventory item'));
    }
  };

  // ── Quick-add handlers ──────────────────────────────────────────────────────

  const openQuickAdd = (type) => {
    setQuickAdd({ type, form: { ...QUICK_ADD_CONFIGS[type].emptyForm }, error: '' });
  };

  const closeQuickAdd = () => {
    setQuickAdd({ type: null, form: {}, error: '' });
  };

  const handleQuickAddChange = (e) => {
    const { name, value } = e.target;
    setQuickAdd((prev) => ({ ...prev, form: { ...prev.form, [name]: value } }));
  };

  const handleQuickAddSubmit = async () => {
    const cfg = QUICK_ADD_CONFIGS[quickAdd.type];
    setQuickAdd((prev) => ({ ...prev, error: '' }));
    try {
      const res = await cfg.create(quickAdd.form);
      const newItem = res.data;
      const listRes = await cfg.getAll();
      listSetters[cfg.setter](toArray(listRes.data, cfg.label));
      setFormData((prev) => ({ ...prev, [cfg.formField]: newItem.id }));
      closeQuickAdd();
    } catch (e) {
      setQuickAdd((prev) => ({ ...prev, error: e?.response?.data?.error || 'Save failed' }));
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const SelectWithAdd = ({ type, children }) => (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <Box sx={{ flex: 1 }}>{children}</Box>
      <Button
        variant="outlined"
        onClick={() => openQuickAdd(type)}
        sx={{ minWidth: '40px', height: '40px', px: 1 }}
        tabIndex={-1}
      >
        <AddIcon />
      </Button>
    </Box>
  );

  const itemTypeOptions = useMemo(() => [
    { id: '', label: '— None —' },
    ...itemTypes.map((t) => ({ id: t.id, label: t.name })),
  ], [itemTypes]);

  const manufacturerOptions = useMemo(() => [
    { id: '', label: '— None —' },
    ...manufacturers.map((m) => ({ id: m.id, label: m.name })),
  ], [manufacturers]);

  const unitOptions = useMemo(() => [
    { id: '', label: '— None —' },
    ...units.map((u) => ({ id: u.id, label: `${u.name}${u.abbreviation ? ` (${u.abbreviation})` : ''}` })),
  ], [units]);

  const supplierOptions = useMemo(() => [
    { id: '', label: '— None —' },
    ...suppliers.map((s) => ({
      id: s.id,
      label: `${s.name}${s.city || s.state ? ` — ${[s.city, s.state].filter(Boolean).join(', ')}` : ''}`,
    })),
  ], [suppliers]);

  const lowStockItems = items.filter((item) => item.low_stock);


  const quickCfg = quickAdd.type ? QUICK_ADD_CONFIGS[quickAdd.type] : null;

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pt: 0.5 }}>
          <Typography variant="h4">Inventory</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Item
          </Button>
        </Box>

        {lowStockItems.length > 0 && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1.5 }}>
            {lowStockItems.length} item(s) are low on stock and need reordering
          </Alert>
        )}

        {loadError ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {loadError}
          </Alert>
        ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Reorder Level</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={8} />}
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell align="center" sx={actionsKabobCellSx}>
                    <RowActionsMenu
                      actions={[
                        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(item) },
                        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(item.id), color: 'error' },
                      ]}
                    />
                  </TableCell>
                  <TableCell>{item.item_name}</TableCell>
                  <TableCell>{item.item_type || '-'}</TableCell>
                  <TableCell>{item.manufacturer_name || '-'}</TableCell>
                  <TableCell>
                    {item.quantity} {item.unit || ''}
                  </TableCell>
                  <TableCell>
                    {item.reorder_level != null ? `${item.reorder_level} ${item.unit || ''}` : '-'}
                  </TableCell>
                  <TableCell>{item.supplier_name || '-'}</TableCell>
                  <TableCell>
                    {item.low_stock ? (
                      <Chip label="Low Stock" size="small" color="warning" />
                    ) : (
                      <Chip label="In Stock" size="small" color="success" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Add / Edit Item dialog ─────────────────────────────────────────── */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Inventory Item' : 'Add New Inventory Item'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              name="item_name"
              label="Item Name"
              value={formData.item_name}
              onChange={handleChange}
              required
              fullWidth
              size="small"
            />

            <SelectWithAdd type="itemType">
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                options={itemTypeOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                filterOptions={createPrefixFilter((o) => o.label)}
                value={itemTypeOptions.find((o) => o.id === formData.item_type_id) ?? itemTypeOptions[0]}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, item_type_id: newValue?.id ?? '' }))
                }
                renderInput={(params) => <TextField {...params} label="Item Type" />}
              />
            </SelectWithAdd>

            <SelectWithAdd type="manufacturer">
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                options={manufacturerOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                filterOptions={createPrefixFilter((o) => o.label)}
                value={manufacturerOptions.find((o) => o.id === formData.manufacturer_id) ?? manufacturerOptions[0]}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, manufacturer_id: newValue?.id ?? '' }))
                }
                renderInput={(params) => <TextField {...params} label="Manufacturer" />}
              />
            </SelectWithAdd>

            <TextField
              name="quantity"
              label="Current Quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              fullWidth
              size="small"
            />

            <SelectWithAdd type="unit">
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                options={unitOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                filterOptions={createPrefixFilter((o) => o.label)}
                value={unitOptions.find((o) => o.id === formData.unit_id) ?? unitOptions[0]}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, unit_id: newValue?.id ?? '' }))
                }
                renderInput={(params) => <TextField {...params} label="Unit" />}
              />
            </SelectWithAdd>

            <TextField
              name="reorder_level"
              label="Reorder Level"
              type="number"
              value={formData.reorder_level}
              onChange={handleChange}
              fullWidth
              size="small"
            />

            <SelectWithAdd type="supplier">
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                options={supplierOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                filterOptions={createPrefixFilter((o) => o.label)}
                value={supplierOptions.find((o) => o.id === formData.supplier_id) ?? supplierOptions[0]}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, supplier_id: newValue?.id ?? '' }))
                }
                renderInput={(params) => <TextField {...params} label="Supplier" />}
              />
            </SelectWithAdd>

            <TextField
              size="small"
              name="notes"
              label="Notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quick-add sub-dialog ───────────────────────────────────────────── */}
      <Dialog open={!!quickAdd.type} onClose={closeQuickAdd} maxWidth="xs" fullWidth>
        <DialogTitle>Add New {quickCfg?.label}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {quickAdd.error && <Alert severity="error">{quickAdd.error}</Alert>}
            {quickCfg?.fields.map((f) =>
              f.type === 'select' ? (
                <Autocomplete
                  key={f.key}
                  fullWidth
                  size="small"
                  disableClearable
                  options={[{ value: '', label: 'None' }, ...f.options]}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v?.value}
                  filterOptions={createPrefixFilter((o) => o.label)}
                  value={
                    [{ value: '', label: 'None' }, ...f.options].find((o) => o.value === (quickAdd.form[f.key] ?? ''))
                    ?? { value: '', label: 'None' }
                  }
                  onChange={(e, newValue) =>
                    setQuickAdd((prev) => ({ ...prev, form: { ...prev.form, [f.key]: newValue?.value ?? '' } }))
                  }
                  renderInput={(params) => <TextField {...params} label={f.label} required={f.required} />}
                />
              ) : (
                <TextField
                  key={f.key}
                  name={f.key}
                  label={f.label}
                  value={quickAdd.form[f.key] ?? ''}
                  onChange={handleQuickAddChange}
                  required={f.required}
                  fullWidth
                  size="small"
                />
              )
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeQuickAdd}>Cancel</Button>
          <Button onClick={handleQuickAddSubmit} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Inventory Item"
        message="Permanently delete this item? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default InventoryPage;
